import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'contexts/ConfigContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { TPODataContext } from 'contexts/TPOContext';
import { getAllPlansParser } from 'parser/tradePromoOptimization/plansParser';
import { parseKpiTableData } from 'parser/tradePromoOptimization/eventsParser';
const errorMessage: string = 'Something went wrong!';

function useFetchKpiTableData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    extras = {},
}: HookInputProps) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    const [count, setCount] = useState(0);
    const { dimensionConfigData, kpiConfigData } = useContext(TPODataContext);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
                setCount(0);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { data: eventsList, total_count } = payload;
            let mergedDataRes: any = [];
            if (eventsList?.length === 0) {
                throw 'No Data';
            }
            const groupByMapping: Record<string, any> = {};
            extras?.dimensionDropdownList?.forEach((item: any) => {
                groupByMapping[item?.value ?? ''] = {
                    label: item?.tableHeader,
                    dataKey: item?.dataKey,
                    sortKey: item?.sortKey,
                };
            });

            const {
                result: parsedData,
                kpiColumns,
                nonKpiColumns,
            } = parseKpiTableData(
                eventsList,
                kpiConfigData,
                variables?.group_by,
                groupByMapping,
                extras?.eventViewType,
            );
            //if  offset is different from default offset value, it means request coming from infinite scroll
            if (variables?.offset !== 0 && Array.isArray(data?.result)) {
                //merge  old data, new data
                mergedDataRes = [...data?.result, ...parsedData];
            } else mergedDataRes = parsedData;
            setData({ result: mergedDataRes, kpiColumns, nonKpiColumns });
            setCount(total_count || 0);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export { useFetchKpiTableData };
